import React, { useState, useEffect, useRef } from 'react'
import apiClient, { API_ENDPOINTS, getUserInfoSync, BASE_URL } from '../../config/api'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import html2canvas from 'html2canvas'
import Swal from 'sweetalert2'

const formatNumber = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatCurrency = (value) => `$${formatNumber(value)}`

const formatPercentage = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const getPreferredBranchId = (userInfo) => {
  if (!userInfo) {
    return null
  }

  if (typeof userInfo.branch === 'number') {
    return userInfo.branch
  }

  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.id === 'number') {
      return userInfo.branch.id
    }
  }

  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const getPreferredBranchName = (userInfo) => {
  if (!userInfo) {
    return null
  }

  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.name === 'string' && userInfo.branch.name.trim()) {
      return userInfo.branch.name
    }
  }

  return userInfo.branch_name || userInfo.branchName || null
}

const Reports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [salesTrends, setSalesTrends] = useState(null)
  const [stockMovement, setStockMovement] = useState(null)
  const [salesReport, setSalesReport] = useState(null)
  const [stockReport, setStockReport] = useState(null)
  const [cashierPerformance, setCashierPerformance] = useState(null)
  const [returnsRate, setReturnsRate] = useState(null)
  const [expenses, setExpenses] = useState([])

  // Chart refs
  const dailySalesChartRef = useRef(null)
  const weeklySalesChartRef = useRef(null)
  const paymentMethodChartRef = useRef(null)
  const salesByBranchChartRef = useRef(null)
  const topProductsChartRef = useRef(null)
  const adjustmentsByTypeChartRef = useRef(null)
  const dailyAdjustmentsChartRef = useRef(null)
  const warehouseEntriesChartRef = useRef(null)
  const branchEntriesChartRef = useRef(null)
  const transfersByTypeChartRef = useRef(null)
  const dailyTransfersChartRef = useRef(null)
  const warehouseStockChartRef = useRef(null)
  const branchStockChartRef = useRef(null)
  const cashierRevenueChartRef = useRef(null)
  const cashierDailyTrendChartRef = useRef(null)
  const cashierPaymentMethodChartRef = useRef(null)
  const reportsSectionRef = useRef(null)

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true)
      setError(null)
      try {
        const userInfo = getUserInfoSync()
        const branchId = getPreferredBranchId(userInfo)

        // Build query parameters for returns rate API
        const returnsRateParams = {}
        if (branchId) {
          returnsRateParams.branch_id = branchId
        }

        // Build query parameters for expenses API
        const expensesParams = {}
        if (branchId) {
          expensesParams.branch_id = branchId
        }

        const [branchReportsRes, cashierPerformanceRes, returnsRateRes, expensesRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.BRANCH_REPORTS),
          apiClient.get(API_ENDPOINTS.CASHIER_PERFORMANCE),
          apiClient.get(API_ENDPOINTS.SALES_RETURNS_RATE, { params: returnsRateParams }).catch(err => {
            // If returns rate API fails, log but don't fail the whole report
            console.warn('Error fetching returns rate:', err)
            return { data: null }
          }),
          apiClient.get(API_ENDPOINTS.EXPENSES, { params: expensesParams }).catch(err => {
            // If expenses API fails, log but don't fail the whole report
            console.warn('Error fetching expenses:', err)
            return { data: [] }
          })
        ])
        const data = branchReportsRes.data

        // Map the new API response structure to existing state variables
        setSalesTrends(data.sales_trends)
        setStockMovement(data.stock_movement)
        setSalesReport(data.sales_report)
        setStockReport(data.stock_report)
        setCashierPerformance(cashierPerformanceRes.data)
        setReturnsRate(returnsRateRes.data)

        // Handle expenses data (can be array or object with results)
        const expensesData = Array.isArray(expensesRes.data) ? expensesRes.data : expensesRes.data?.results || []
        setExpenses(expensesData)
      } catch (err) {
        console.error('Error fetching reports:', err)
        setError('Failed to load reports. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchReports()
  }, [])

  // Initialize charts when data is available
  useEffect(() => {
    if (!window.ApexCharts) return

    const destroyCharts = () => {
      const refs = [
        dailySalesChartRef, weeklySalesChartRef, paymentMethodChartRef,
        salesByBranchChartRef, topProductsChartRef, adjustmentsByTypeChartRef,
        dailyAdjustmentsChartRef, warehouseEntriesChartRef, branchEntriesChartRef,
        transfersByTypeChartRef, dailyTransfersChartRef, warehouseStockChartRef,
        branchStockChartRef, cashierRevenueChartRef, cashierDailyTrendChartRef,
        cashierPaymentMethodChartRef
      ]
      refs.forEach(ref => {
        if (ref.current?.chart) {
          ref.current.chart.destroy()
          ref.current.chart = null
        }
      })
    }

    destroyCharts()

    // Daily Sales Trend - Line Chart
    if (salesTrends?.daily_sales_trend?.length && dailySalesChartRef.current) {
      const chart = new window.ApexCharts(dailySalesChartRef.current, {
        series: [
          {
            name: 'Revenue',
            data: salesTrends.daily_sales_trend.map(item => {
              const val = Number(item.total_revenue ?? 0)
              return isNaN(val) ? 0 : val
            })
          },
          {
            name: 'Transaction Count',
            data: salesTrends.daily_sales_trend.map(item => {
              const val = Number(item.transaction_count ?? 0)
              return isNaN(val) ? 0 : val
            })
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: salesTrends.daily_sales_trend.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Revenue' },
            labels: {
              formatter: (val) => formatCurrency(val)
            }
          },
          {
            opposite: true,
            title: { text: 'Count' },
            labels: {
              formatter: (val) => Math.round(val)
            }
          }
        ],
        tooltip: {
          y: [
            { formatter: (val) => formatCurrency(val) },
            { formatter: (val) => `${Math.round(val)} transactions` }
          ]
        },
        colors: ['#4154f1', '#2eca6a'],
        stroke: { curve: 'smooth', width: 3 }
      })
      chart.render()
      dailySalesChartRef.current.chart = chart
    }

    // Weekly Sales Trend - Column Chart
    if (salesTrends?.weekly_sales_trend?.length && weeklySalesChartRef.current) {
      const chart = new window.ApexCharts(weeklySalesChartRef.current, {
        series: [{
          name: 'Revenue',
          data: salesTrends.weekly_sales_trend.map(item => {
            const val = Number(item.total_revenue ?? 0)
            return isNaN(val) ? 0 : val
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        xaxis: {
          categories: salesTrends.weekly_sales_trend.map(item => {
            const date = new Date(item.week)
            return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          })
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1']
      })
      chart.render()
      weeklySalesChartRef.current.chart = chart
    }

    // Sales by Payment Method - Pie Chart
    if (salesTrends?.sales_by_payment_method?.length && paymentMethodChartRef.current) {
      const chart = new window.ApexCharts(paymentMethodChartRef.current, {
        series: salesTrends.sales_by_payment_method.map(item => {
          const revenue = Number(item.total_revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'pie',
          height: 350,
          toolbar: { show: true }
        },
        labels: salesTrends.sales_by_payment_method.map(item =>
          item.payment_method.charAt(0).toUpperCase() + item.payment_method.slice(1)
        ),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      paymentMethodChartRef.current.chart = chart
    }

    // Sales by Branch - Donut Chart
    if (salesTrends?.sales_by_branch?.length && salesByBranchChartRef.current) {
      const chart = new window.ApexCharts(salesByBranchChartRef.current, {
        series: salesTrends.sales_by_branch.map(item => {
          const revenue = Number(item.total_revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: salesTrends.sales_by_branch.map(item => item.branch_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      salesByBranchChartRef.current.chart = chart
    }

    // Top Selling Products - Bar Chart
    if (salesTrends?.top_selling_products?.length && topProductsChartRef.current) {
      const chart = new window.ApexCharts(topProductsChartRef.current, {
        series: [{
          name: 'Revenue',
          data: salesTrends.top_selling_products.map(item => {
            const revenue = Number(item.total_revenue ?? 0)
            return isNaN(revenue) ? 0 : revenue
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        yaxis: {
          categories: salesTrends.top_selling_products.map(item => item.product_name),
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1']
      })
      chart.render()
      topProductsChartRef.current.chart = chart
    }

    // Adjustments by Type - Pie Chart
    if (stockMovement?.adjustments_by_type?.length && adjustmentsByTypeChartRef.current) {
      const chart = new window.ApexCharts(adjustmentsByTypeChartRef.current, {
        series: stockMovement.adjustments_by_type.map(item => {
          const count = Number(item.count ?? 0)
          return isNaN(count) ? 0 : count
        }),
        chart: {
          type: 'pie',
          height: 350,
          toolbar: { show: true }
        },
        labels: stockMovement.adjustments_by_type.map(item =>
          item.adjustment_type.charAt(0).toUpperCase() + item.adjustment_type.slice(1)
        ),
        tooltip: {
          y: {
            formatter: (val) => `${val} adjustments`
          }
        },
        colors: ['#2eca6a', '#dc3545', '#ffc107', '#6c757d']
      })
      chart.render()
      adjustmentsByTypeChartRef.current.chart = chart
    }

    // Daily Adjustments - Line Chart
    if (stockMovement?.daily_adjustments?.length && dailyAdjustmentsChartRef.current) {
      const chart = new window.ApexCharts(dailyAdjustmentsChartRef.current, {
        series: [
          {
            name: 'Quantity',
            data: stockMovement.daily_adjustments.map(item => {
              const qty = Number(item.total_quantity ?? 0)
              return isNaN(qty) ? 0 : qty
            })
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: stockMovement.daily_adjustments.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: {
          labels: {
            formatter: (val) => formatNumber(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatNumber(val)
          }
        },
        colors: ['#4154f1'],
        stroke: { curve: 'smooth', width: 3 }
      })
      chart.render()
      dailyAdjustmentsChartRef.current.chart = chart
    }

    // Warehouse Entries - Column Chart
    if (stockMovement?.warehouse_entries?.length && warehouseEntriesChartRef.current) {
      const chart = new window.ApexCharts(warehouseEntriesChartRef.current, {
        series: [
          {
            name: 'Quantity',
            data: stockMovement.warehouse_entries.map(item => {
              const qty = Number(item.total_quantity ?? 0)
              return isNaN(qty) ? 0 : qty
            })
          },
          {
            name: 'Cost',
            data: stockMovement.warehouse_entries.map(item => {
              const cost = Number(item.total_cost ?? 0)
              return isNaN(cost) ? 0 : cost
            })
          }
        ],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        xaxis: {
          categories: stockMovement.warehouse_entries.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Quantity' },
            labels: {
              formatter: (val) => formatNumber(val)
            }
          },
          {
            opposite: true,
            title: { text: 'Cost' },
            labels: {
              formatter: (val) => formatCurrency(val)
            }
          }
        ],
        tooltip: {
          y: [
            { formatter: (val) => formatNumber(val) },
            { formatter: (val) => formatCurrency(val) }
          ]
        },
        colors: ['#4154f1', '#2eca6a']
      })
      chart.render()
      warehouseEntriesChartRef.current.chart = chart
    }

    // Branch Entries - Column Chart
    if (stockMovement?.branch_entries?.length && branchEntriesChartRef.current) {
      const chart = new window.ApexCharts(branchEntriesChartRef.current, {
        series: [
          {
            name: 'Quantity',
            data: stockMovement.branch_entries.map(item => {
              const qty = Number(item.total_quantity ?? 0)
              return isNaN(qty) ? 0 : qty
            })
          },
          {
            name: 'Cost',
            data: stockMovement.branch_entries.map(item => {
              const cost = Number(item.total_cost ?? 0)
              return isNaN(cost) ? 0 : cost
            })
          }
        ],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          }
        },
        xaxis: {
          categories: stockMovement.branch_entries.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Quantity' },
            labels: {
              formatter: (val) => formatNumber(val)
            }
          },
          {
            opposite: true,
            title: { text: 'Cost' },
            labels: {
              formatter: (val) => formatCurrency(val)
            }
          }
        ],
        tooltip: {
          y: [
            { formatter: (val) => formatNumber(val) },
            { formatter: (val) => formatCurrency(val) }
          ]
        },
        colors: ['#ff771d', '#f72585']
      })
      chart.render()
      branchEntriesChartRef.current.chart = chart
    }

    // Transfers by Type - Donut Chart
    if (stockMovement?.transfers_by_type?.length && transfersByTypeChartRef.current) {
      // Group by transfer type
      const typeGroups = {}
      stockMovement.transfers_by_type.forEach(item => {
        const type = item.transfer_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        if (!typeGroups[type]) {
          typeGroups[type] = 0
        }
        typeGroups[type] += Number(item.count ?? 0)
      })

      const chart = new window.ApexCharts(transfersByTypeChartRef.current, {
        series: Object.values(typeGroups),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: Object.keys(typeGroups),
        tooltip: {
          y: {
            formatter: (val) => `${val} transfers`
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d']
      })
      chart.render()
      transfersByTypeChartRef.current.chart = chart
    }

    // Daily Transfers - Line Chart
    if (stockMovement?.daily_transfers?.length && dailyTransfersChartRef.current) {
      const chart = new window.ApexCharts(dailyTransfersChartRef.current, {
        series: [
          {
            name: 'Count',
            data: stockMovement.daily_transfers.map(item => {
              const count = Number(item.count ?? 0)
              return isNaN(count) ? 0 : count
            })
          },
          {
            name: 'Quantity',
            data: stockMovement.daily_transfers.map(item => {
              const qty = Number(item.total_quantity ?? 0)
              return isNaN(qty) ? 0 : qty
            })
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: stockMovement.daily_transfers.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Count' },
            labels: { formatter: (val) => Math.round(val) }
          },
          {
            opposite: true,
            title: { text: 'Quantity' },
            labels: {
              formatter: (val) => formatNumber(val)
            }
          }
        ],
        tooltip: {
          y: [
            { formatter: (val) => `${Math.round(val)} transfers` },
            { formatter: (val) => formatNumber(val) }
          ]
        },
        colors: ['#4154f1', '#2eca6a'],
        stroke: { curve: 'smooth', width: 3 }
      })
      chart.render()
      dailyTransfersChartRef.current.chart = chart
    }

    // Warehouse Stock - Bar Chart (Top products)
    if (stockReport?.warehouse_stock?.length && warehouseStockChartRef.current) {
      const topWarehouse = stockReport.warehouse_stock
        .sort((a, b) => (b.total_quantity || 0) - (a.total_quantity || 0))
        .slice(0, 10)

      const chart = new window.ApexCharts(warehouseStockChartRef.current, {
        series: [{
          name: 'Quantity',
          data: topWarehouse.map(item => {
            const qty = Number(item.total_quantity ?? 0)
            return isNaN(qty) ? 0 : qty
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          labels: {
            formatter: (val) => formatNumber(val)
          }
        },
        yaxis: {
          categories: topWarehouse.map(item => `${item.product_name} (${item.warehouse_name})`),
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatNumber(val)
          }
        },
        colors: ['#4154f1']
      })
      chart.render()
      warehouseStockChartRef.current.chart = chart
    }

    // Branch Stock - Bar Chart
    if (stockReport?.branch_stock?.length && branchStockChartRef.current) {
      const chart = new window.ApexCharts(branchStockChartRef.current, {
        series: [{
          name: 'Quantity',
          data: stockReport.branch_stock.map(item => {
            const qty = Number(item.total_quantity ?? 0)
            return isNaN(qty) ? 0 : qty
          })
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          labels: {
            formatter: (val) => formatNumber(val)
          }
        },
        yaxis: {
          categories: stockReport.branch_stock.map(item => `${item.product_name} (${item.branch_name})`),
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatNumber(val)
          }
        },
        colors: ['#2eca6a']
      })
      chart.render()
      branchStockChartRef.current.chart = chart
    }

    // Cashier Performance - Revenue by Cashier (Bar Chart)
    if (cashierPerformance?.cashiers?.length && cashierRevenueChartRef.current) {
      const chart = new window.ApexCharts(cashierRevenueChartRef.current, {
        series: [{
          name: 'Revenue',
          data: cashierPerformance.cashiers.map(item => {
            const revenue = Number(item.total_sales_amount ?? 0)
            return isNaN(revenue) ? 0 : revenue
          })
        }],
        chart: {
          type: 'bar',
          height: 300,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        yaxis: {
          categories: cashierPerformance.cashiers.map(item => item.cashier_name),
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1']
      })
      chart.render()
      cashierRevenueChartRef.current.chart = chart
    }

    // Cashier Daily Trend - Line Chart
    if (cashierPerformance?.daily_trends?.length && cashierDailyTrendChartRef.current) {
      const chart = new window.ApexCharts(cashierDailyTrendChartRef.current, {
        series: [
          {
            name: 'Revenue',
            data: cashierPerformance.daily_trends.map(item => {
              const val = Number(item.total_revenue ?? 0)
              return isNaN(val) ? 0 : val
            })
          },
          {
            name: 'Profit',
            data: cashierPerformance.daily_trends.map(item => {
              const val = Number(item.total_profit ?? 0)
              return isNaN(val) ? 0 : val
            })
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: cashierPerformance.daily_trends.map(item => {
            const date = new Date(item.date)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: {
          labels: {
            formatter: (val) => formatCurrency(val)
          }
        },
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a'],
        stroke: { curve: 'smooth', width: 3 }
      })
      chart.render()
      cashierDailyTrendChartRef.current.chart = chart
    }

    // Cashier Payment Method Breakdown - Pie Chart
    if (cashierPerformance?.payment_method_breakdown?.length && cashierPaymentMethodChartRef.current) {
      const chart = new window.ApexCharts(cashierPaymentMethodChartRef.current, {
        series: cashierPerformance.payment_method_breakdown.map(item => {
          const revenue = Number(item.total_revenue ?? 0)
          return isNaN(revenue) ? 0 : revenue
        }),
        chart: {
          type: 'pie',
          height: 300,
          toolbar: { show: true }
        },
        labels: cashierPerformance.payment_method_breakdown.map(item =>
          item.payment_method.charAt(0).toUpperCase() + item.payment_method.slice(1)
        ),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7']
      })
      chart.render()
      cashierPaymentMethodChartRef.current.chart = chart
    }

    return () => {
      destroyCharts()
    }
  }, [salesTrends, stockMovement, stockReport, cashierPerformance])

  if (loading) {
    return (
      <>
        <div className="pagetitle">
          <h1>Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading reports...</span>
            </div>
          </div>
        </section>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className="pagetitle">
          <h1>Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </section>
      </>
    )
  }

  const generatePDF = async () => {
    if (!reportsSectionRef.current) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to capture report section.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    setGeneratingPDF(true)

    try {
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we capture the report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const canvas = await html2canvas(reportsSectionRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: reportsSectionRef.current.scrollWidth,
        windowHeight: reportsSectionRef.current.scrollHeight
      })

      const pageWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const margin = 10 // Margin in mm
      const contentWidth = pageWidth - (margin * 2)
      const contentHeight = pageHeight - (margin * 2)

      // Calculate image dimensions to fit content width
      const imgWidth = contentWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      const doc = new jsPDF('portrait', 'mm', 'a4')
      let yPosition = 0
      let pageNumber = 0

      while (yPosition < imgHeight) {
        if (pageNumber > 0) {
          doc.addPage()
        }

        // Calculate the portion of image to show on this page
        const remainingHeight = imgHeight - yPosition
        const pageImgHeight = Math.min(remainingHeight, contentHeight)

        // Calculate source coordinates in pixels
        const sourceY = (yPosition / imgHeight) * canvas.height
        const sourceHeight = (pageImgHeight / imgHeight) * canvas.height

        // Create a temporary canvas for this page slice
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = canvas.width
        pageCanvas.height = sourceHeight
        const pageCtx = pageCanvas.getContext('2d')

        // Draw the slice of the original canvas
        pageCtx.drawImage(
          canvas,
          0, sourceY, // Source x, y in pixels
          canvas.width, sourceHeight, // Source width, height in pixels
          0, 0, // Destination x, y
          pageCanvas.width, pageCanvas.height // Destination width, height
        )

        const pageImgData = pageCanvas.toDataURL('image/png')
        const scaledPageHeight = (pageCanvas.height * contentWidth) / pageCanvas.width

        // Add image to PDF with margins
        doc.addImage(
          pageImgData,
          'PNG',
          margin,
          margin,
          contentWidth,
          scaledPageHeight
        )

        yPosition += contentHeight
        pageNumber++
      }

      const fileName = `Branch_Reports_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'The report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    } finally {
      setGeneratingPDF(false)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1>Branch Reports</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">Reports</li>
              </ol>
            </nav>
          </div>
          <button
            onClick={generatePDF}
            className="btn btn-primary"
            style={{ height: 'fit-content' }}
            disabled={generatingPDF}
          >
            {generatingPDF ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generating PDF...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-pdf me-2"></i>
                Download PDF
              </>
            )}
          </button>
        </div>
      </div>

      <section className="section" ref={reportsSectionRef}>
        {/* Sales Summary */}
        {salesReport?.summary && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-graph-up-arrow text-primary me-2"></i>
                  Sales Summary
                </h5>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Revenue</h6>
                    <h4 className="mb-0 text-primary">{formatCurrency(salesReport.summary.total_revenue || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Transactions</h6>
                    <h4 className="mb-0 text-success">{salesReport.summary.total_transactions || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Profit</h6>
                    <h4 className="mb-0 text-info">{formatCurrency(salesReport.summary.total_profit || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Profit Margin</h6>
                    <h4 className="mb-0 text-warning">{salesReport.summary.profit_margin_percentage?.toFixed(2) || 0}%</h4>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Returns Rate Section */}
        {returnsRate && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-counterclockwise text-primary me-2"></i>
                  Returns Rate Analysis
                </h5>
              </div>
            </div>

            {/* Returns Rate Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sold Quantity</h6>
                    <h4 className="mb-0 text-primary">{formatNumber(returnsRate.metrics?.total_sold_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sales Value</h6>
                    <h4 className="mb-0 text-success">{formatCurrency(returnsRate.metrics?.total_sales_value || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Returned Quantity</h6>
                    <h4 className="mb-0 text-danger">{formatNumber(returnsRate.metrics?.total_returned_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Refund Amount</h6>
                    <h4 className="mb-0 text-warning">{formatCurrency(returnsRate.metrics?.total_refund_amount || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Return Rate Metrics */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Quantity Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.quantity_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {returnsRate.metrics?.total_returned_quantity || 0} of {returnsRate.metrics?.total_sold_quantity || 0} items returned
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Value Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.value_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {formatCurrency(returnsRate.metrics?.total_refund_amount || 0)} of {formatCurrency(returnsRate.metrics?.total_sales_value || 0)} refunded
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Returned Products */}
            {returnsRate.top_returned_products?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Top Returned Products</h5>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th className="text-end">Returned Quantity</th>
                                <th className="text-end">Refund Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {returnsRate.top_returned_products.map((product, idx) => (
                                <tr key={product.product_id || idx}>
                                  <td className="fw-semibold">{product.product_name}</td>
                                  <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                  <td className="text-end text-danger fw-semibold">{formatNumber(product.total_returned_quantity)}</td>
                                  <td className="text-end text-warning fw-semibold">{formatCurrency(product.total_refund_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Information */}
            {returnsRate.filters && (() => {
              const userInfo = getUserInfoSync()
              const branchName = getPreferredBranchName(userInfo)
              return (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <h6 className="text-muted mb-2">Report Period</h6>
                        <p className="mb-0">
                          <strong>Start Date:</strong> {returnsRate.filters.start_date ? new Date(returnsRate.filters.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} &nbsp;|&nbsp;
                          <strong>End Date:</strong> {returnsRate.filters.end_date ? new Date(returnsRate.filters.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                          {branchName && (
                            <> &nbsp;|&nbsp; <strong>Branch:</strong> {branchName}</>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}
          </>
        )}

        {/* Sales Trends Section */}
        {salesTrends && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-graph-up-arrow text-primary me-2"></i>
                  Sales Trends
                </h5>
              </div>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Daily Sales Trend</h5>
                    {salesTrends.daily_sales_trend?.length ? (
                      <>
                        <div ref={dailySalesChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th className="text-end">Revenue</th>
                                  <th className="text-end">Transactions</th>
                                  <th className="text-end">Items Sold</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesTrends.daily_sales_trend.map((item, idx) => {
                                  const date = new Date(item.day)
                                  return (
                                    <tr key={idx}>
                                      <td>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                                      <td className="text-end fw-semibold">{formatCurrency(item.total_revenue)}</td>
                                      <td className="text-end">{item.transaction_count}</td>
                                      <td className="text-end">{formatNumber(item.total_items_sold)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-calendar-x fs-3 mb-2 d-block"></i>
                        <span>No daily sales data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Weekly Sales Trend</h5>
                    {salesTrends.weekly_sales_trend?.length ? (
                      <>
                        <div ref={weeklySalesChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Week</th>
                                  <th className="text-end">Revenue</th>
                                  <th className="text-end">Transactions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesTrends.weekly_sales_trend.map((item, idx) => {
                                  const date = new Date(item.week)
                                  return (
                                    <tr key={idx}>
                                      <td>Week of {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                      <td className="text-end fw-semibold">{formatCurrency(item.total_revenue)}</td>
                                      <td className="text-end">{item.transaction_count}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-calendar-week fs-3 mb-2 d-block"></i>
                        <span>No weekly sales data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method and Branch Sales */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Sales by Payment Method</h5>
                    {salesTrends.sales_by_payment_method?.length ? (
                      <>
                        <div ref={paymentMethodChartRef} className="flex-grow-1"></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Payment Method</th>
                                  <th className="text-end">Revenue</th>
                                  <th className="text-end">Transactions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesTrends.sales_by_payment_method.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.payment_method.charAt(0).toUpperCase() + item.payment_method.slice(1)}</td>
                                    <td className="text-end fw-semibold">{formatCurrency(item.total_revenue)}</td>
                                    <td className="text-end">{item.transaction_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-credit-card fs-3 mb-2 d-block"></i>
                          <span>No payment method data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Sales by Branch</h5>
                    {salesTrends.sales_by_branch?.length ? (
                      <>
                        <div ref={salesByBranchChartRef} className="flex-grow-1"></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Branch</th>
                                  <th className="text-end">Revenue</th>
                                  <th className="text-end">Transactions</th>
                                  <th className="text-end">Items Sold</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesTrends.sales_by_branch.map((item, idx) => (
                                  <tr key={item.branch_id || idx}>
                                    <td>{item.branch_name}</td>
                                    <td className="text-end fw-semibold">{formatCurrency(item.total_revenue)}</td>
                                    <td className="text-end">{item.transaction_count}</td>
                                    <td className="text-end">{formatNumber(item.total_items_sold)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-shop fs-3 mb-2 d-block"></i>
                          <span>No branch sales data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Selling Products */}
            {salesTrends.top_selling_products?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Top Selling Products</h5>
                      <div ref={topProductsChartRef}></div>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th className="text-end">Quantity Sold</th>
                                <th className="text-end">Revenue</th>
                                <th className="text-end">Cost</th>
                                <th className="text-end">Profit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {salesTrends.top_selling_products.map((product, idx) => (
                                <tr key={product.product_id || idx}>
                                  <td className="fw-semibold">{product.product_name}</td>
                                  <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                  <td className="text-end">{formatNumber(product.total_quantity_sold)}</td>
                                  <td className="text-end fw-semibold">{formatCurrency(product.total_revenue)}</td>
                                  <td className="text-end">{formatCurrency(product.total_cost)}</td>
                                  <td className="text-end text-success fw-semibold">{formatCurrency(product.profit)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Stock Movement Section */}
        {stockMovement && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-left-right text-primary me-2"></i>
                  Stock Movement
                </h5>
              </div>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Adjustments by Type</h5>
                    {stockMovement.adjustments_by_type?.length ? (
                      <>
                        <div ref={adjustmentsByTypeChartRef} className="flex-grow-1"></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Type</th>
                                  <th className="text-end">Count</th>
                                  <th className="text-end">Total Quantity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockMovement.adjustments_by_type.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.adjustment_type.charAt(0).toUpperCase() + item.adjustment_type.slice(1)}</td>
                                    <td className="text-end">{item.count}</td>
                                    <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-pie-chart fs-3 mb-2 d-block"></i>
                          <span>No adjustment data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Daily Adjustments</h5>
                    {stockMovement.daily_adjustments?.length ? (
                      <>
                        <div ref={dailyAdjustmentsChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th>Type</th>
                                  <th className="text-end">Count</th>
                                  <th className="text-end">Quantity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockMovement.daily_adjustments.map((item, idx) => {
                                  const date = new Date(item.day)
                                  return (
                                    <tr key={idx}>
                                      <td>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                      <td>{item.adjustment_type.charAt(0).toUpperCase() + item.adjustment_type.slice(1)}</td>
                                      <td className="text-end">{item.count}</td>
                                      <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-calendar-x fs-3 mb-2 d-block"></i>
                        <span>No daily adjustment data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Warehouse and Branch Entries */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Warehouse Entries</h5>
                    {stockMovement.warehouse_entries?.length ? (
                      <>
                        <div ref={warehouseEntriesChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th className="text-end">Entries</th>
                                  <th className="text-end">Quantity</th>
                                  <th className="text-end">Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockMovement.warehouse_entries.map((item, idx) => {
                                  const date = new Date(item.day)
                                  return (
                                    <tr key={idx}>
                                      <td>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                      <td className="text-end">{item.entry_count}</td>
                                      <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                      <td className="text-end fw-semibold">{formatCurrency(item.total_cost)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-box-seam fs-3 mb-2 d-block"></i>
                        <span>No warehouse entry data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Branch Entries</h5>
                    {stockMovement.branch_entries?.length ? (
                      <>
                        <div ref={branchEntriesChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th className="text-end">Entries</th>
                                  <th className="text-end">Quantity</th>
                                  <th className="text-end">Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockMovement.branch_entries.map((item, idx) => {
                                  const date = new Date(item.day)
                                  return (
                                    <tr key={idx}>
                                      <td>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                      <td className="text-end">{item.entry_count}</td>
                                      <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                      <td className="text-end fw-semibold">{formatCurrency(item.total_cost)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-shop fs-3 mb-2 d-block"></i>
                        <span>No branch entry data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Transfers */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Transfers by Type</h5>
                    {stockMovement.transfers_by_type?.length ? (
                      <>
                        <div ref={transfersByTypeChartRef} className="flex-grow-1"></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Type</th>
                                  <th>Status</th>
                                  <th className="text-end">Count</th>
                                  <th className="text-end">Quantity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockMovement.transfers_by_type.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.transfer_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                                    <td><span className={`badge ${item.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>{item.status}</span></td>
                                    <td className="text-end">{item.count}</td>
                                    <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-arrow-left-right fs-3 mb-2 d-block"></i>
                          <span>No transfer data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Daily Transfers</h5>
                    {stockMovement.daily_transfers?.length ? (
                      <>
                        <div ref={dailyTransfersChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th className="text-end">Count</th>
                                  <th className="text-end">Quantity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockMovement.daily_transfers.map((item, idx) => {
                                  const date = new Date(item.day)
                                  return (
                                    <tr key={idx}>
                                      <td>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                      <td className="text-end">{item.count}</td>
                                      <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-calendar-x fs-3 mb-2 d-block"></i>
                        <span>No daily transfer data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Stock Report Section */}
        {stockReport && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-box-seam text-primary me-2"></i>
                  Stock Report
                </h5>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Warehouse Products</h6>
                    <h4 className="mb-0 text-primary">{stockReport.warehouse_summary?.total_products || 0}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Warehouse Quantity</h6>
                    <h4 className="mb-0 text-success">{formatNumber(stockReport.warehouse_summary?.total_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Warehouse Cost</h6>
                    <h4 className="mb-0 text-info">{formatCurrency(stockReport.warehouse_summary?.total_cost || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Low Stock Items</h6>
                    <h4 className="mb-0 text-warning">{stockReport.branch_summary?.low_stock_count || 0}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Charts */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Warehouse Stock (Top 10)</h5>
                    {stockReport.warehouse_stock?.length ? (
                      <>
                        <div ref={warehouseStockChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Product</th>
                                  <th>Warehouse</th>
                                  <th className="text-end">Quantity</th>
                                  <th className="text-end">Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockReport.warehouse_stock
                                  .sort((a, b) => (b.total_quantity || 0) - (a.total_quantity || 0))
                                  .slice(0, 10)
                                  .map((item, idx) => (
                                    <tr key={idx}>
                                      <td>{item.product_name}</td>
                                      <td>{item.warehouse_name}</td>
                                      <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                      <td className="text-end fw-semibold">{formatCurrency(item.total_cost)}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-box-seam fs-3 mb-2 d-block"></i>
                        <span>No warehouse stock data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5 className="card-title">Branch Stock</h5>
                    {stockReport.branch_stock?.length ? (
                      <>
                        <div ref={branchStockChartRef}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Product</th>
                                  <th>Branch</th>
                                  <th className="text-end">Quantity</th>
                                  <th className="text-end">Selling Value</th>
                                  <th className="text-end">Profit</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockReport.branch_stock.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.product_name}</td>
                                    <td>{item.branch_name}</td>
                                    <td className="text-end">{formatNumber(item.total_quantity)}</td>
                                    <td className="text-end">{formatCurrency(item.total_selling_value)}</td>
                                    <td className="text-end text-success">{formatCurrency(item.potential_profit)}</td>
                                    <td>
                                      {item.is_low_stock ? (
                                        <span className="badge bg-warning">Low Stock</span>
                                      ) : (
                                        <span className="badge bg-success">In Stock</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-shop fs-3 mb-2 d-block"></i>
                        <span>No branch stock data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Cashier Performance Section */}
        {cashierPerformance && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-person-check text-primary me-2"></i>
                  Cashier Performance
                </h5>
              </div>
            </div>

            {/* Cashier Summary Cards */}
            {cashierPerformance.cashiers?.length > 0 && (
              <div className="row g-3 mb-4">
                {cashierPerformance.cashiers.map((cashier, idx) => (
                  <div key={cashier.cashier_id || idx} className="col-md-4 col-6">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <h6 className="text-muted mb-2">{cashier.cashier_name}</h6>
                        <p className="text-muted small mb-3">{cashier.cashier_email}</p>
                        <div className="row g-2">
                          <div className="col-6">
                            <small className="text-muted d-block">Revenue</small>
                            <strong className="text-primary">{formatCurrency(cashier.total_sales_amount)}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Profit</small>
                            <strong className="text-success">{formatCurrency(cashier.total_profit)}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Transactions</small>
                            <strong>{cashier.transaction_count}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Avg Sale</small>
                            <strong>{formatCurrency(cashier.average_sale_value)}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Profit Margin</small>
                            <strong className="text-info">{cashier.profit_margin_percentage?.toFixed(2) || 0}%</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Items/Transaction</small>
                            <strong>{formatNumber(cashier.items_per_transaction)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cashier Performance Charts */}
            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Revenue by Cashier</h5>
                    {cashierPerformance.cashiers?.length ? (
                      <>
                        <div ref={cashierRevenueChartRef} className="flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Cashier</th>
                                  <th className="text-end">Revenue</th>
                                  <th className="text-end">Profit</th>
                                  <th className="text-end">Transactions</th>
                                  <th className="text-end">Profit Margin</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cashierPerformance.cashiers.map((cashier, idx) => (
                                  <tr key={cashier.cashier_id || idx}>
                                    <td>
                                      <div>
                                        <strong>{cashier.cashier_name}</strong>
                                        <br />
                                        <small className="text-muted">{cashier.cashier_email}</small>
                                      </div>
                                    </td>
                                    <td className="text-end fw-semibold">{formatCurrency(cashier.total_sales_amount)}</td>
                                    <td className="text-end text-success">{formatCurrency(cashier.total_profit)}</td>
                                    <td className="text-end">{cashier.transaction_count}</td>
                                    <td className="text-end">{cashier.profit_margin_percentage?.toFixed(2) || 0}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-person-x fs-3 mb-2 d-block"></i>
                          <span>No cashier data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">Payment Method Breakdown</h5>
                    {cashierPerformance.payment_method_breakdown?.length ? (
                      <>
                        <div ref={cashierPaymentMethodChartRef} className="flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}></div>
                        <div className="mt-3">
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Payment Method</th>
                                  <th className="text-end">Revenue</th>
                                  <th className="text-end">Transactions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cashierPerformance.payment_method_breakdown.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.payment_method.charAt(0).toUpperCase() + item.payment_method.slice(1)}</td>
                                    <td className="text-end fw-semibold">{formatCurrency(item.total_revenue)}</td>
                                    <td className="text-end">{item.transaction_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <i className="bi bi-credit-card fs-3 mb-2 d-block"></i>
                          <span>No payment method data available</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Trends */}
            {cashierPerformance.daily_trends?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Daily Performance Trend</h5>
                      <div ref={cashierDailyTrendChartRef}></div>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th className="text-end">Transactions</th>
                                <th className="text-end">Revenue</th>
                                <th className="text-end">Cost</th>
                                <th className="text-end">Profit</th>
                                <th className="text-end">Items Sold</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cashierPerformance.daily_trends.map((item, idx) => {
                                const date = new Date(item.date)
                                return (
                                  <tr key={idx}>
                                    <td>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                                    <td className="text-end">{item.transaction_count}</td>
                                    <td className="text-end fw-semibold">{formatCurrency(item.total_revenue)}</td>
                                    <td className="text-end">{formatCurrency(item.total_cost)}</td>
                                    <td className="text-end text-success fw-semibold">{formatCurrency(item.total_profit)}</td>
                                    <td className="text-end">{formatNumber(item.total_items_sold)}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Cashier Performance Table */}
            {cashierPerformance.cashiers?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Detailed Cashier Performance</h5>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Cashier</th>
                              <th className="text-end">Total Revenue</th>
                              <th className="text-end">Total Cost</th>
                              <th className="text-end">Total Profit</th>
                              <th className="text-end">Profit Margin</th>
                              <th className="text-end">Transactions</th>
                              <th className="text-end">Avg Sale Value</th>
                              <th className="text-end">Items Sold</th>
                              <th className="text-end">Items/Transaction</th>
                              <th className="text-end">Discounts</th>
                              <th className="text-end">Tax</th>
                              <th className="text-end">Refunds</th>
                              <th className="text-end">Voids</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cashierPerformance.cashiers.map((cashier, idx) => (
                              <tr key={cashier.cashier_id || idx}>
                                <td>
                                  <div>
                                    <strong>{cashier.cashier_name}</strong>
                                    <br />
                                    <small className="text-muted">{cashier.cashier_email}</small>
                                  </div>
                                </td>
                                <td className="text-end fw-semibold">{formatCurrency(cashier.total_sales_amount)}</td>
                                <td className="text-end">{formatCurrency(cashier.total_cost)}</td>
                                <td className="text-end text-success fw-semibold">{formatCurrency(cashier.total_profit)}</td>
                                <td className="text-end">{cashier.profit_margin_percentage?.toFixed(2) || 0}%</td>
                                <td className="text-end">{cashier.transaction_count}</td>
                                <td className="text-end">{formatCurrency(cashier.average_sale_value)}</td>
                                <td className="text-end">{formatNumber(cashier.total_items_sold)}</td>
                                <td className="text-end">{formatNumber(cashier.items_per_transaction)}</td>
                                <td className="text-end">{formatCurrency(cashier.total_discount)}</td>
                                <td className="text-end">{formatCurrency(cashier.total_tax)}</td>
                                <td className="text-end">
                                  {cashier.refund_count > 0 ? (
                                    <span className="badge bg-warning">{cashier.refund_count}</span>
                                  ) : (
                                    <span className="text-muted">0</span>
                                  )}
                                </td>
                                <td className="text-end">
                                  {cashier.void_count > 0 ? (
                                    <span className="badge bg-danger">{cashier.void_count}</span>
                                  ) : (
                                    <span className="text-muted">0</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Expenses Section */}
        {expenses.length > 0 && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-receipt-cutoff text-primary me-2"></i>
                  Expenses Report
                </h5>
              </div>
            </div>

            {/* Expenses Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Expenses</h6>
                    <h4 className="mb-0 text-danger">
                      {formatCurrency(expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Records</h6>
                    <h4 className="mb-0 text-primary">{expenses.length}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Average Expense</h6>
                    <h4 className="mb-0 text-info">
                      {formatCurrency(
                        expenses.length > 0
                          ? expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0) / expenses.length
                          : 0
                      )}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Categories</h6>
                    <h4 className="mb-0 text-warning">
                      {new Set(expenses.map((exp) => exp.category_name).filter(Boolean)).size}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses Table */}
            <div className="row g-4 mb-4">
              <div className="col-12">
                <div className="card shadow-sm border-0">
                  <div className="card-body">
                    <h5 className="card-title">Expense Details</h5>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Description</th>
                            <th>Category</th>
                            <th>Branch</th>
                            <th>Warehouse</th>
                            <th className="text-end">Amount</th>
                            <th className="text-end">Date</th>
                            <th>Recorded By</th>
                            <th className="text-end">Attachment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenses.map((expense) => (
                            <tr key={expense.id}>
                              <td>
                                <div className="fw-semibold text-dark">{expense.description || 'No description'}</div>
                              </td>
                              <td>{expense.category_name || '—'}</td>
                              <td>{expense.branch_name || '—'}</td>
                              <td>{expense.warehouse_name || '—'}</td>
                              <td className="text-end fw-semibold">{formatCurrency(Number(expense.amount) || 0)}</td>
                              <td className="text-end text-muted">
                                {expense.incurred_on
                                  ? new Date(expense.incurred_on).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })
                                  : '—'}
                              </td>
                              <td>
                                <small className="text-muted">{expense.created_by_email || 'Unknown'}</small>
                              </td>
                              <td className="text-end">
                                {expense.attachment ? (
                                  <a
                                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
                                    href={
                                      expense.attachment.startsWith('http://') || expense.attachment.startsWith('https://')
                                        ? expense.attachment
                                        : expense.attachment.startsWith('/')
                                          ? `${BASE_URL}${expense.attachment}`
                                          : `${BASE_URL}/${expense.attachment}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <i className="bi bi-paperclip" />
                                    <span className="text-truncate" style={{ maxWidth: 100 }}>
                                      {expense.attachment.split('/').pop() || 'attachment'}
                                    </span>
                                  </a>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td colSpan="4" className="fw-bold text-end">
                              Total:
                            </td>
                            <td className="text-end fw-bold text-danger">
                              {formatCurrency(expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0))}
                            </td>
                            <td colSpan="3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default Reports
